import type {Schedule} from '../types/domain'
import React, {useContext, useEffect, useState} from 'react'
import {Alert, Box, Button, IconButton, Paper, Snackbar, Stack, Typography} from '@mui/material'
import {Field, Form, useFormState} from 'react-final-form'
import RffTextFieldAdapter from '../form/RffTextFieldAdapter'
import {scheduleErrors, scheduleWithOrderedBreaks} from './scheduleErrors'
import {FORM_ERROR} from 'final-form'
import arrayMutators from 'final-form-arrays'
import {blindLevelMutators} from './blindLevelMutators'
import {FieldArray} from 'react-final-form-arrays'
import {useCloudFunctionsService} from '../backend/CloudFunctionsServiceContext'
import {useNavigate} from 'react-router-dom'
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutlined'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutlined'
import {ScheduleContext} from './ScheduleContext'
import {AuthContext} from '../auth/AuthContext'
import {clearFailedScheduleDraft, readFailedScheduleDraft, writeFailedScheduleDraft} from './failedScheduleDraft'

function PendingDraftObserver({onChange}: {onChange: (values: Schedule) => void}) {
  const {values} = useFormState<Schedule>({subscription: {values: true}})
  useEffect(() => onChange(values), [values, onChange])
  return null
}

const parseNumericInput = (value: string) => value === '' ? undefined : Number(value)

const sections = [
  {name: 'blinds', title: 'Blinds', singular: 'Round', defaults: {smallBlind: 0, bigBlind: 0, ante: 0}, fields: [
    {name: 'smallBlind', label: 'Small Blind', required: true},
    {name: 'bigBlind', label: 'Big Blind', required: true},
    {name: 'ante', label: 'Ante', required: false},
  ]},
  {name: 'breaks', title: 'Breaks', singular: 'Break', defaults: {afterLevelNumber: 0, length: 0}, fields: [
    {name: 'afterLevelNumber', label: 'After Level #', required: true},
    {name: 'length', label: 'Length', required: true},
  ]},
  {name: 'chips', title: 'Chips', singular: 'Chip', defaults: {color: '', value: 0}, fields: [
    {name: 'color', label: 'Color', required: true},
    {name: 'value', label: 'Value', required: true},
  ]},
]

export default function BlindsSchedule({schedule}: {schedule: Schedule}) {
  const {currentUser} = useContext(AuthContext)
  return <ScheduleForm key={JSON.stringify([currentUser?.uid, schedule.name])} schedule={schedule} ownerUid={currentUser?.uid}/>
}

function ScheduleForm({schedule, ownerUid}: {schedule: Schedule; ownerUid?: string}) {
  const {updateSchedule} = useCloudFunctionsService()
  const {updateSchedules} = useContext(ScheduleContext)
  const navigate = useNavigate()
  const [restoredDraft] = useState(() => readFailedScheduleDraft(ownerUid, schedule.name))
  const [pending, setPending] = useState(!!restoredDraft)
  const [storedInTab, setStoredInTab] = useState(!!restoredDraft)
  const [dismissed, setDismissed] = useState(restoredDraft?.dismissed ?? false)
  const preserveDraft = React.useCallback((values: Schedule) => {
    setStoredInTab(!!ownerUid && writeFailedScheduleDraft({ownerUid, originalName: schedule.name, values, dismissed}))
  }, [ownerUid, schedule.name, dismissed])
  const dismissError = () => {
    setDismissed(true)
  }
  async function onSubmit(values: Schedule) {
    try {
      await updateSchedule(scheduleWithOrderedBreaks(values))
    } catch {
      setDismissed(false)
      setPending(true)
      setStoredInTab(!!ownerUid && writeFailedScheduleDraft({ownerUid, originalName: schedule.name, values, dismissed: false}))
      return {[FORM_ERROR]: 'Unable to save schedule. Please try again.'}
    }
    clearFailedScheduleDraft(ownerUid, schedule.name)
    setPending(false)
    try {
      await updateSchedules()
      await navigate('/schedules')
    } catch {
      return {[FORM_ERROR]: 'Schedule saved, but unable to open the updated schedule list. Please reload.'}
    }
  }
  return <Stack spacing={3} sx={{maxWidth: 1200, mx: 'auto', width: '100%'}}>
    <Snackbar open={pending && !dismissed} autoHideDuration={null} anchorOrigin={{vertical: 'top', horizontal: 'center'}} sx={{top: {xs: 80, sm: 80}}}>
      <Alert severity="error" variant="filled" onClose={dismissError}>
        {storedInTab
          ? 'Unable to save schedule. Your edits are saved in this tab. You can leave this page and return in the same tab to retry with Save. Closing this tab discards these edits. Closing this message keeps them.'
          : 'Unable to save schedule. Your edits are preserved on this page, but could not be saved in this tab. Keep this page open and use Save to try again.'}
      </Alert>
    </Snackbar>
    <Box><Typography variant="h3" component="h1">Editing Schedule {schedule.name}</Typography>
      <Typography color="text.secondary" sx={{mt: 1}}>Set the pace, plan the breaks, and choose your chips.</Typography></Box>
    <Form<Schedule> validate={scheduleErrors} onSubmit={onSubmit} mutators={{...arrayMutators, ...blindLevelMutators}} initialValues={restoredDraft?.values ?? schedule}
      render={({handleSubmit, pristine, submitting, hasValidationErrors, submitError, form}) => <Stack component="form" onSubmit={handleSubmit} noValidate spacing={3}>
        {pending && <PendingDraftObserver onChange={preserveDraft}/>}
        {submitError && !pending && <Alert severity="warning">{submitError}</Alert>}
        <Paper component="section" aria-label="Schedule details" sx={{p: {xs: 2.5, sm: 3.5}}}>
          <Typography variant="h5" component="h2" sx={{mb: 3}}>Schedule details</Typography>
          <Stack direction={{xs: 'column', sm: 'row'}} spacing={3}>
            <Field label="Schedule Name" name="name" required component={RffTextFieldAdapter} fullWidth/>
            <Field label="Level Length" name="levelLength" required component={RffTextFieldAdapter} showError fullWidth type="number" parse={parseNumericInput}/>
          </Stack>
        </Paper>
        {sections.map(section => <Paper key={section.name} component="section" aria-label={section.title} sx={{p: {xs: 2.5, sm: 3.5}}}>
          <Typography variant="h5" component="h2" sx={{mb: 3}}>{section.title}</Typography>
          <FieldArray name={section.name}>{({fields, meta}) => <Stack spacing={2.5}>
            {typeof meta.error === 'string' && <Typography color="error" role="alert">{meta.error}</Typography>}
            {fields.map((entry, index) => <Box key={entry} sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', md: '100px 1fr auto'}, gap: 2, alignItems: 'center', borderBottom: 1, borderColor: 'divider', pb: 2.5}}>
              <Typography variant="overline">{section.singular} #{index + 1}</Typography>
              <Box sx={{display: 'grid', gridTemplateColumns: `repeat(${section.fields.length}, minmax(0, 1fr))`, gap: 1.5}}>
                {section.fields.map(field => <Field key={field.name} name={`${entry}.${field.name}`} label={field.label}
                  component={RffTextFieldAdapter} showError required={field.required} fullWidth size="small" type={field.name === 'color' ? 'text' : 'number'}
                  parse={value => field.name === 'color' ? value : parseNumericInput(value)}/>) }
              </Box>
              <Stack direction="row" spacing={1}>
                <IconButton aria-label="add" title={`Insert ${section.singular.toLowerCase()} before this entry`} color="primary" onClick={() => section.name === 'blinds' ? form.mutators.insertBlind(index, section.defaults) : fields.insert(index, section.defaults)}><AddCircleOutlineIcon/></IconButton>
                <IconButton aria-label="subtract" title={`Remove ${section.singular.toLowerCase()}`} color="inherit" onClick={() => section.name === 'blinds' ? form.mutators.removeBlind(index) : fields.remove(index)}><RemoveCircleOutlineIcon/></IconButton>
              </Stack>
            </Box>)}
            <Button variant="outlined" onClick={() => fields.push(section.defaults)} sx={{alignSelf: 'flex-start'}}>Add {section.singular.toLowerCase()}</Button>
          </Stack>}</FieldArray>
        </Paper>)}
        <Stack component="footer" direction="row" spacing={2} sx={{pb: 3}}>
          <Button disabled={submitting} variant="outlined" onClick={() => navigate(-1)}>Cancel</Button>
          <Button disabled={submitting || (pristine && !pending) || hasValidationErrors} variant="contained" type="submit">Save</Button>
        </Stack>
      </Stack>}/>
  </Stack>
}
